import { Button, ButtonThemesEnum } from '@propertyfinder/button';
import { IconArrowLeftThick, IconArrowRightThick } from '@propertyfinder/icons';
import { TextField } from '@propertyfinder/text-field';
import { ToggleComponent } from '@propertyfinder/toggle';
import preact, { Fragment, FunctionComponent, h } from 'preact';
import { useCallback, useMemo, useState } from 'preact/hooks';

import { configGetEnv } from 'common/helper/config/get-env';
import { configGetLanguage } from 'common/helper/config/get-language';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { ClaimTransactionPropsInterface } from 'common/module/property-report/claim-transaction/props.interface';
import { ClaimTransactionStageType } from 'common/module/property-report/claim-transaction/stage.type';
import { PropertyReportDocumentType } from 'common/module/property-report/document.type';
import { EligibilityValidationFormTemplate } from 'common/module/property-report/eligibility-validation-form/template';
import { PropertyReportStatsTrackerFactory } from 'common/module/property-report/stats/tracker.factory';
import { PropertyReportTooltipModalModalContentType } from 'common/module/property-report/tooltip-modal/modal-content.type';
import { PropertyReportTooltipModalTemplate } from 'common/module/property-report/tooltip-modal/template';
import { PropertyReportUploadTemplate } from 'common/module/property-report/upload/template';

import { tooltipModalInformationContent } from '../tooltip-modal/information-content';

export const ClaimTransactionComponent: FunctionComponent<ClaimTransactionPropsInterface> = ({
  onValidate,
  onGoBack,
  onChange,
  state,
  onDocumentClear,
  onChangeCmtFile,
  claimTransactionAttachments,
  stage,
  cmtFieldErrors,
  eligibilityCheckStatus,
  onSubmitClaimTransaction,
  status,
  agentInformation,
  propertyId,
}) => {
  const [isListingOwner, setIsListingOwner] = useState(false);
  const [modalType, setModalType] = useState<PropertyReportTooltipModalModalContentType>('default');

  const onConfirmOwnListing = useCallback(
    (e: Event) => {
      e.preventDefault();
      setIsListingOwner((prevState) => {
        PropertyReportStatsTrackerFactory().onToggleListingUser(!isListingOwner, agentInformation, propertyId);
        return !prevState;
      });
    },
    [isListingOwner]
  );

  const handleValidation = useCallback(() => {
    onValidate(isListingOwner);
  }, [isListingOwner]);

  const handleBack = useCallback(() => {
    onGoBack();
  }, []);

  const handleFile = useCallback(
    (e: Event, documentType: PropertyReportDocumentType): void => {
      onChangeCmtFile(e, documentType);
    },
    [onChangeCmtFile]
  );
  const handleSubmitClaim = useCallback((e: Event) => {
    onSubmitClaimTransaction(e);
  }, []);

  const renderModalSelection = useMemo(() => {
    const ModalContentComponent = tooltipModalInformationContent[modalType].content
      ? tooltipModalInformationContent[modalType].content
      : null;
    const modalTitle = ModalContentComponent ? tooltipModalInformationContent[modalType].title.replace(':', '') : null;
    return (
      <PropertyReportTooltipModalTemplate
        title={modalTitle}
        hideContent={(): void => setModalType('default')}
        platform={configGetEnv().platform}
      >
        <ModalContentComponent />
      </PropertyReportTooltipModalTemplate>
    );
  }, [modalType]);

  function renderValidate(): preact.JSX.Element {
    return (
      <div className='property-report-2'>
        <div className='property-report-2__checked-field row-center'>
          <p className='property-report-2__checked-field--text'>{i18nTranslate('ReportProperty_ThisIsMyListing')}</p>
          <ToggleComponent
            onClick={(): void => onConfirmOwnListing(event)}
            checked={isListingOwner}
            className='property-report-2__checked-field--toggle'
          />
        </div>
        <EligibilityValidationFormTemplate
          state={state}
          onChange={onChange}
          onClick={handleValidation}
          isAgentListing={isListingOwner}
          cmtFieldErrors={cmtFieldErrors}
          isValidatingField={eligibilityCheckStatus.checking}
        />
      </div>
    );
  }

  function renderSubmit(): preact.JSX.Element {
    return (
      <div className='property-report-2'>
        <div className='property-report-2__validated-container'>
          <TextField disabled value={state.agentEmail} placeholder={i18nTranslate('Auth_EmailLabel')} />
          {!isListingOwner && (
            <TextField
              disabled
              value={state.listingUrl}
              placeholder={i18nTranslate('ReportProperty_EnterUrlAddress')}
            />
          )}
        </div>
        <div className='property-report-2__info row-center'>
          <p className='property-report-2__info--text'>{i18nTranslate('ReportProperty_UploadFollowingDocuments')}</p>
        </div>
        <div className='property-report-2__uploader-container'>
          <PropertyReportUploadTemplate
            file={claimTransactionAttachments.proofOfOwnership}
            title={i18nTranslate('ClaimTransaction_ProofOfOwnership')}
            onUploadFile={(e): void => handleFile(e, 'proofOfOwnership')}
            onDocumentClear={(): void => onDocumentClear('proofOfOwnership')}
          />

          {cmtFieldErrors.proofOfOwnership && (
            <div className='property-report-2__invalid'>{cmtFieldErrors.proofOfOwnership}</div>
          )}

          <PropertyReportUploadTemplate
            file={claimTransactionAttachments.proofOfTransaction}
            title={i18nTranslate('ClaimTransaction_ProofOfTransaction')}
            onUploadFile={(e): void => handleFile(e, 'proofOfTransaction')}
            onDocumentClear={(): void => onDocumentClear('proofOfTransaction')}
          />
          {cmtFieldErrors.proofOfTransaction && (
            <div className='property-report-2__invalid'>{cmtFieldErrors.proofOfTransaction}</div>
          )}
        </div>
        <div className='property-report-2__info row-center'>
          <p className='property-report-2__info--text'>{i18nTranslate('ReportProperty_IfPropertyListedWOthers')}</p>
        </div>
        <div className='property-report-2__input-area'>
          <TextField
            htmlAttributes={{
              type: 'text',
              'data-testid': 'other-listing-input',
            }}
            placeholder={i18nTranslate('ReportProperty_EnterCompetitorsListing')}
            value={state.additionalRefListing}
            onInput={(e: InputEvent): void => onChange(e, 'additionalRefListing')}
          />
        </div>
        {cmtFieldErrors.errorFromServer && (
          <div className='property-report-2__invalid'>{cmtFieldErrors.errorFromServer}</div>
        )}

        <div className='property-report-2__form-submit'>
          <Button
            theme={ButtonThemesEnum.LINK}
            className='property-report-2__form-submit--back row-center'
            onClick={handleBack}
            htmlAttributes={{
              name: 'cmt-back-btn',
            }}
            type={'button'}
          >
            {configGetLanguage().isRtl ? (
              <IconArrowRightThick className='property-report-2__form-submit--back-text' />
            ) : (
              <IconArrowLeftThick className='property-report-2__form-submit--back-text' />
            )}
            <span className='property-report-2__form-submit--back-text'>{i18nTranslate('Back')}</span>
          </Button>
          <Button
            theme={ButtonThemesEnum.PRIMARY}
            className='property-report-2__form-submit--claim-button'
            onClick={handleSubmitClaim}
            loading={status.submitting}
          >
            {i18nTranslate('ReportProperty_SubmitRequest')}
          </Button>
        </div>
      </div>
    );
  }

  const stageMap: Record<ClaimTransactionStageType, preact.JSX.Element> = {
    validate: renderValidate(),
    submit: renderSubmit(),
  };

  return (
    <Fragment>
      {stageMap[stage]}
      {modalType !== 'default' ? renderModalSelection : null}
    </Fragment>
  );
};
